import PromoTile from '@srcDS/components/molecules/promoTile';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useMeasure } from 'react-use';

import ProductTile from '../productTile/ProductTile';
import ArticleTile from '@srcDS/components/molecules/journal/articleTile/ArticleTile';

import { formattedImagesArray, formatVariants, getProductDetails, getTags } from '@srcDS/utils/helpers';
import { useCategoryContext } from '../../templates/category/context/useCategoryContext';
import { useTouchDevice } from '@srcDS/components/organisms/categoryProductList/hooks/useTouchDevice';
import { useUserContext } from '@srcDS/components/organisms/categoryProductList/hooks/useUserContext';
import { useProductViewport } from '@srcDS/components/organisms/categoryProductList/hooks/useProductViewport';
import { useScrollPosition } from '@srcDS/components/organisms/categoryProductList/hooks/useScrollPosition';
import { useNostoSearchTracking } from '@srcDS/components/organisms/categoryProductList/hooks/useNostoSearchTracking';
import { useNostoContext } from '@srcDS/provider/nosto/NostoContext';

import { theme } from './_vars';
import defaultClasses from './CategoryProductList.module.scss';
import mergeClasses from '../../../utils/shallowMerge';

const CategoryProductList = ({
  customClasses = null,
  products = [],
  gridType = 'wide',
  placement,
  categoryTitle,
  listName,
  nostoProductClick,
  nostoPlacement,
}) => {
  const [containerRef] = useMeasure();

  const { isSSR, layoutCount, triggerRoute, isSearchPage, isCuratedForYou, searchPhrase } = useCategoryContext() as any;
  const { nostoRecordSearchClick } = useNostoContext();
  const isTouch = useTouchDevice(isSSR);
  const [productHeight, setProductHeight] = useState<number | undefined>();
  const { loggedIn, customerGroupId } = useUserContext();
  const checkIfTileInViewport = useProductViewport(products, gridType);
  const registerScrollPosition = useScrollPosition(isSSR);

  useNostoSearchTracking({ products, searchPhrase, triggerRoute });

  const styles = mergeClasses(defaultClasses, customClasses);
  const customStyle = clsx(styles.root, styles[`layout_${gridType}`]);

  const renderTile = (product: any, index: number) => {
    // Page indicator
    if (product.type === 'pageIndicator') {
      return (
        <h1 key={`page-${product.page}`}>
          Page {product.page}
          <input ref={product.ref} type="hidden" value={product.page} />
        </h1>
      );
    }

    // Article tile
    if (product.variants?.[0].attributes.type === 'article') {
      const articleItem = product.variants?.[0].attributes;
      return (
        <ArticleTile
          key={`article-${index}`}
          {...articleItem}
          isSSR={isSSR}
          section={articleItem.categories?.[0]}
          className={styles.articleItem}
          isWrapperMedia
          customClasses={{
            articleDescription: styles.articleDescription,
            mediaBlock: styles.mediaBlock,
            bottom: styles.articleBottomBlock,
          }}
        />
      );
    }

    // Promo tile
    if (product.type === 'promo') {
      return (
        <PromoTile
          key={`promo-${index}`}
          textStyles={product.textStyles}
          preloadImage={checkIfTileInViewport(product, 'promo')}
          {...product.data}
          layout={layoutCount}
          theme={theme}
          productsBefore={index}
          productHeight={productHeight}
        />
      );
    }

    // Product tile
    const { variants, initialVariant, regularPrice, discountedPrice, onSale, currentDiscount, finalSale } =
      getProductDetails(product, isSearchPage, loggedIn, customerGroupId);

    if (!product || !initialVariant) return null;

    return (
      <ProductTile
        isSearchPage={isSearchPage}
        setProductHeight={setProductHeight}
        listName={listName}
        isTouch={isTouch}
        position={index}
        preloadImage={checkIfTileInViewport(product, 'product')}
        productId={product.productId}
        categoryTitle={categoryTitle}
        parentProduct={product}
        remoteImage={false}
        productType={product.type || product.slug}
        item={initialVariant}
        gridType={gridType}
        data-id={product.productId}
        data-sku={initialVariant.sku}
        key={`${product.productId || initialVariant.sku}_${index}`}
        productName={product?.name}
        variants={!isSearchPage ? variants : formatVariants(product.variants?.[0].attributes.variants)}
        finalSale={finalSale}
        placement={placement}
        enableTouchOnMobile={true}
        images={formattedImagesArray(initialVariant)}
        regularPrice={regularPrice}
        discountedPrice={discountedPrice}
        onSale={onSale}
        inOnStock={initialVariant.inOnStock}
        promoContent={currentDiscount}
        currency={initialVariant.currency}
        url={product._url}
        name={product.name}
        tags={getTags(initialVariant, product, isSearchPage, false)}
        label={initialVariant?.attributes?.newarrival ? 'New' : ''}
        theme={theme}
        nostoPlacement={nostoPlacement}
        nostoCallback={nostoProductClick}
        pathParam={product.pathParam}
        onClick={(ref) => {
          registerScrollPosition(ref);
        }}
        enableGallery={true}
        type={isSearchPage || isCuratedForYou ? 'serp' : 'category'}
        nostoRecordSearchClick={nostoRecordSearchClick}
      />
    );
  };
  return (
    <div ref={containerRef} className={customStyle}>
      {Array.isArray(products) && products.length > 0 && products.map(renderTile)}
    </div>
  );
};

export default CategoryProductList;
